import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { api, Module, Lesson } from "../lib/api";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import {
  CheckCircle2,
  Circle,
  Lock,
  PlayCircle,
  Trophy,
  Flame,
  BookOpen,
  ArrowLeft,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";

export default function PersonalizedModuleView() {
  const { moduleId } = useParams<{ moduleId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [module, setModule] = useState<Module | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Check if coming from personalized roadmap
  const fromRoadmap = searchParams.get("fromRoadmap") === "true";

  useEffect(() => {
    loadModule();
  }, [moduleId]);

  const loadModule = async () => {
    if (!moduleId) {
      setError("Module ID is required");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const moduleData = await api.getModuleById(moduleId);

      if (!moduleData) {
        setError("Module not found");
        return;
      }

      setModule(moduleData);
    } catch (err) {
      console.error("Failed to load module:", err);
      setError(err instanceof Error ? err.message : "Failed to load module");
    } finally {
      setLoading(false);
    }
  };

  const getLessonTypeIcon = (lessonType?: string) => {
    switch (lessonType) {
      case "learn":
        return <BookOpen className="w-4 h-4 text-blue-500" />;
      case "forge":
        return <Flame className="w-4 h-4 text-orange-500" />;
      case "challenge":
        return <Trophy className="w-4 h-4 text-purple-500" />;
      default:
        return <BookOpen className="w-4 h-4 text-gray-500" />;
    }
  };

  const getLessonStatus = (lesson: Lesson, index: number): string => {
    // If coming from personalized roadmap, first lesson should be unlocked
    if (fromRoadmap && index === 0 && !lesson.progress?.status) {
      return "unlocked";
    }
    return lesson.progress?.status || "locked";
  };

  const getLessonStatusIcon = (lesson: Lesson, index: number) => {
    if (!user) {
      return <Lock className="w-5 h-5 text-gray-400" />;
    }

    const status = getLessonStatus(lesson, index);

    switch (status) {
      case "completed":
        return <CheckCircle2 className="w-5 h-5 text-green-500" />;
      case "in_progress":
        return <PlayCircle className="w-5 h-5 text-blue-500" />;
      case "unlocked":
        return <Circle className="w-5 h-5 text-yellow-500" />;
      case "locked":
      default:
        return <Lock className="w-5 h-5 text-gray-400" />;
    }
  };

  const getLessonStatusColor = (lesson: Lesson, index: number) => {
    if (!user)
      return "bg-gray-100 dark:bg-gray-800 border-gray-300 dark:border-gray-600";

    const status = getLessonStatus(lesson, index);

    switch (status) {
      case "completed":
        return "bg-green-50 dark:bg-green-900/20 border-green-500";
      case "in_progress":
        return "bg-blue-50 dark:bg-blue-900/20 border-blue-500";
      case "unlocked":
        return "bg-yellow-50 dark:bg-yellow-900/20 border-yellow-500";
      case "locked":
      default:
        return "bg-gray-100 dark:bg-gray-800 border-gray-300 dark:border-gray-600";
    }
  };

  const getLessonStatusText = (lesson: Lesson, index: number) => {
    if (!user) return "Locked";

    const status = getLessonStatus(lesson, index);

    switch (status) {
      case "completed":
        return "Completed";
      case "in_progress":
        return "In Progress";
      case "unlocked":
        return "Available";
      case "locked":
      default:
        return "Locked";
    }
  };

  const handleLessonClick = (lesson: Lesson, index: number) => {
    if (!user) {
      navigate("/login");
      return;
    }

    const status = getLessonStatus(lesson, index);
    if (status === "locked") {
      return;
    }

    navigate(
      `/lesson/${lesson.id}?returnTo=${encodeURIComponent("/my-roadmap")}`,
    );
  };

  const getModuleProgress = () => {
    if (!module?.lessons || module.lessons.length === 0) return 0;

    const completedLessons = module.lessons.filter(
      (l) => l.progress?.status === "completed",
    ).length;

    return (completedLessons / module.lessons.length) * 100;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading module...</p>
        </div>
      </div>
    );
  }

  if (error || !module) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <Card className="max-w-md">
          <CardHeader>
            <CardTitle className="text-red-600">Error</CardTitle>
            <CardDescription>
              {error || "Failed to load module"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              variant="outline"
              onClick={() => navigate("/my-roadmap")}
              className="w-full"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to My Roadmap
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const progress = getModuleProgress();
  const completedLessons =
    module.lessons?.filter((l) => l.progress?.status === "completed").length ||
    0;
  const totalLessons = module.lessons?.length || 0;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8 px-4">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate("/my-roadmap")}
          className="text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white p-0 hover:bg-transparent"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to My Roadmap
        </Button>

        {/* Module Card */}
        <Card className="overflow-hidden border-2 border-indigo-500">
          <CardHeader className="bg-indigo-50 dark:bg-indigo-900/20">
            <div className="flex items-center gap-4">
              <div className="text-4xl">{module.icon || "📚"}</div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <CardTitle className="text-xl text-gray-900 dark:text-white">
                    {module.name}
                  </CardTitle>
                  {progress === 100 && (
                    <Badge
                      variant="default"
                      className="bg-green-500 hover:bg-green-600"
                    >
                      <CheckCircle2 className="w-3 h-3 mr-1" />
                      Completed
                    </Badge>
                  )}
                </div>
                <CardDescription className="text-sm text-gray-600 dark:text-gray-400">
                  {module.description}
                </CardDescription>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="mt-4 space-y-2">
              <div className="flex items-center justify-between text-xs">
                <span className="text-gray-600 dark:text-gray-400">
                  {completedLessons} of {totalLessons} lessons completed
                </span>
                <span className="font-semibold text-indigo-600 dark:text-indigo-400">
                  {Math.round(progress)}%
                </span>
              </div>
              <Progress value={progress} className="h-2" />
            </div>
          </CardHeader>

          <CardContent className="pt-6">
            <div className="space-y-3">
              {module.lessons?.map((lesson, index) => {
                const status = getLessonStatus(lesson, index);
                const isLocked = status === "locked";
                const isClickable = !isLocked && user;

                return (
                  <div
                    key={lesson.id}
                    className={`
                      border-2 rounded-lg p-4 transition-all duration-300
                      ${getLessonStatusColor(lesson, index)}
                      ${isClickable ? "cursor-pointer hover:shadow-md hover:scale-[1.01]" : "cursor-not-allowed opacity-75"}
                    `}
                    onClick={() => handleLessonClick(lesson, index)}
                  >
                    <div className="flex items-center gap-3">
                      <div className="flex-shrink-0">
                        {getLessonStatusIcon(lesson, index)}
                      </div>

                      <div className="flex-shrink-0">
                        {getLessonTypeIcon(lesson.lesson_type)}
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-semibold text-sm text-gray-900 dark:text-white">
                            {lesson.title}
                          </h4>
                          <Badge
                            variant="outline"
                            className="text-xs capitalize"
                          >
                            {lesson.lesson_type || "learn"}
                          </Badge>
                        </div>
                        <p className="text-xs text-gray-600 dark:text-gray-400 line-clamp-1">
                          {lesson.description}
                        </p>

                        {lesson.progress?.status === "in_progress" &&
                          lesson.progress.problems_attempted > 0 && (
                            <div className="mt-2 flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                              <span>
                                {lesson.progress.problems_correct}/
                                {lesson.progress.problems_attempted} correct
                              </span>
                              <span>-</span>
                              <span>
                                {lesson.progress.current_accuracy?.toFixed(0)}%
                                accuracy
                              </span>
                            </div>
                          )}
                      </div>

                      <div className="flex-shrink-0">
                        <Badge variant="outline" className="text-xs">
                          {getLessonStatusText(lesson, index)}
                        </Badge>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
